import { KeyboardEvent } from 'react';
import { SyntheticEvent } from 'react-draft-wysiwyg';
import draftToHtml from 'draftjs-to-html';

import { JSONObject } from 'global/graphql/scalars.type';
import RichTextArea from 'lib/form/richtextarea/richtextarea.hooks';
import { ENTER_KEY, ENTER_KEYCODE } from 'lib/strings/strings';
import { EditorWrapper } from 'lib/form/richtextarea/richtextarea.style';
import { hasRichtextContent } from 'lib/form/richtextarea/richtextarea.utils';
import { MentionTag } from 'lib/form/richtextarea/richtextarea.constants';
import { SecondaryLoadingButton } from 'approot/shared/styles/buttons/buttons';
import { PStyled } from 'approot/shared/styles/typography.style';
import { Box } from 'approot/shared/styles/box.style';

type Props = {
  content: JSONObject | null;
  fieldName: string;
  label?: string;
  placeholder?: string;
  isEditMode: boolean;
  isSaving: boolean;
  isLocalEditing: boolean;
  setIsLocalEditing: () => void;
  onBlur?: (e: SyntheticEvent) => void;
  hideSaveButton?: boolean;
  suggestions?: MentionTag[];
  dataSelector?: string;
};

const ContentPageRichtext = ({
  content,
  fieldName,
  label = '',
  placeholder = '',
  isEditMode,
  isSaving,
  isLocalEditing,
  setIsLocalEditing,
  onBlur,
  hideSaveButton = false,
  suggestions,
}: Props) => {
  const contentMarkup = draftToHtml(content);

  const handleOnClick = () => {
    setIsLocalEditing();
  };

  const handleKeyUp = (event: KeyboardEvent<HTMLDivElement>) => {
    event.preventDefault();
    const key = event.key || event.keyCode;
    if (key === ENTER_KEY || key === ENTER_KEYCODE) {
      setIsLocalEditing();
    }
  };

  return (
    <>
      {isEditMode ? (
        <>
          {isLocalEditing ? (
            <>
              <Box $marginBottom={16}>
                <RichTextArea
                  autoFocusOnMount
                  name={fieldName}
                  placeholder={placeholder}
                  initialValue={content}
                  label={label}
                  onBlur={onBlur}
                  suggestions={suggestions}
                />
              </Box>
              {!hideSaveButton && (
                <SecondaryLoadingButton
                  type="submit"
                  isDisabled={isSaving}
                  isLoading={isSaving}
                  data-selector={`save-${fieldName}`}
                >
                  Save
                </SecondaryLoadingButton>
              )}
            </>
          ) : (
            <EditorWrapper
              preEditMode
              onClick={handleOnClick}
              onKeyUp={handleKeyUp}
              tabIndex={0}
              data-selector={fieldName}
              dangerouslySetInnerHTML={{
                __html: hasRichtextContent(content)
                  ? contentMarkup
                  : placeholder,
              }}
            />
          )}
        </>
      ) : (
        <PStyled
          as="div"
          data-selector={`view-${fieldName}`}
          dangerouslySetInnerHTML={{ __html: contentMarkup }}
        />
      )}
    </>
  );
};

export default ContentPageRichtext;
